import React from 'react';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { stromaAnswerCard, stromaInset } from './askPresentation';

/**
 * The proposal: what the agent derived, and the numbers it actually got back.
 *
 * The sample rows are load-bearing. A formula is a claim about meaning; the
 * rows are the only thing on screen that can contradict it, which is why
 * approving is not offered until a dry run has produced some.
 */
export default function ProposedDefinitionCard({
  turn, term, perspectiveName, question,
}: { turn: LandingAgentTurn; term: string; perspectiveName: string; question?: string }) {
  const proposal = useVoyagerStore((s) => s.definitionProposals[turn.id]);
  const saved = useVoyagerStore((s) => s.definitionSaved[turn.id]);
  const busy = useVoyagerStore((s) => !!s.definitionProposing[turn.id]);
  const error = useVoyagerStore((s) => s.definitionCaptureError[turn.id]);
  const approve = useVoyagerStore((s) => s.approveTurnDefinition);
  const clear = useVoyagerStore((s) => s.clearTurnProposal);
  const navigateToDefinitions = useVoyagerStore((s) => s.navigateToDefinitions);

  if (saved) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, ...stromaAnswerCard }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CheckCircleIcon fontSize="small" color="success" />
          <Typography variant="body2">
            <code>{saved.name}</code> saved to {perspectiveName} ·{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              draft, pending data-team review
            </Typography>
          </Typography>
          <Button size="small" onClick={navigateToDefinitions} sx={{ textTransform: 'none' }}>
            View in Definitions
          </Button>
        </Box>
      </Paper>
    );
  }

  if (!proposal) return null;

  const { candidate, validation, existing } = proposal;

  if (existing) {
    const sentBack = existing.status === 'needs_changes';
    // Only the author can revise or withdraw it. Offering "Revise it" to a
    // colleague is a dead end — they click it, go back to the form, and
    // proposing the same name lands them right back here. The way forward for
    // everybody else is the author, not the form. `mine` is decided by the
    // server, which is the only side that knows who is asking.
    const mine = existing.mine === true;
    const author = (existing.author_name || '').trim();
    return (
      <Paper variant="outlined" sx={{ p: 2, ...stromaAnswerCard }}>
        {/* A definition sent back for changes stops answering questions, so
            landing here with a bare "already defined" is a dead end — the
            author needs the reviewer's note to know what to do next. */}
        <Alert severity={sentBack ? 'warning' : 'info'} sx={{ mb: 1 }}>
          {sentBack ? (
            <>
              “{existing.display_name || existing.name}” was sent back for changes,
              so it isn’t answering questions yet.
              {existing.review_note && (
                <Box component="div" sx={{ mt: 0.75, fontStyle: 'italic' }}>
                  “{existing.review_note}”
                  {existing.reviewed_by ? ` — ${existing.reviewed_by}` : ''}
                </Box>
              )}
              <Box component="div" sx={{ mt: 0.75 }}>
                {mine
                  ? 'Edit the definition below and validate it again — that clears '
                    + 'the flag and puts it back in the review queue.'
                  : `Only ${author || 'its author'} can revise it. Until they do, this `
                    + 'term can’t answer questions — ask them, or ask your question '
                    + 'using a metric this perspective already has.'}
              </Box>
            </>
          ) : (
            <>
              “{existing.display_name || existing.name}” is already defined in {perspectiveName}
              {existing.status === 'draft'
                ? (mine
                    ? ' as your draft.'
                    : ` as ${author || 'a colleague'}’s draft, waiting on review — so it `
                      + 'doesn’t answer questions yet. Ask your question again and I’ll '
                      + 'offer to run it as a provisional answer.')
                : '.'}
            </>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">{existing.english}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button size="small" onClick={navigateToDefinitions} sx={{ textTransform: 'none' }}>
            View in Definitions
          </Button>
          <Button size="small" onClick={() => clear(turn.id)} sx={{ textTransform: 'none' }}>
            {sentBack && mine ? 'Revise it' : 'Define something else'}
          </Button>
        </Box>
      </Paper>
    );
  }

  const ok = !!validation?.ok;
  const columns = validation?.columns || [];
  const rows = validation?.rows || [];
  // The SQL ran and returned nothing. Calling that a pass would be true and
  // misleading — with no numbers on screen, nothing about what this
  // definition MEANS has been checked. Still approvable; just not evidence.
  const empty = ok && rows.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, ...stromaAnswerCard }} data-testid="proposed-definition-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {ok && !empty && <CheckCircleIcon fontSize="small" color="success" />}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Proposed definition</Typography>
        {ok && !empty && (
          <Typography variant="caption" color="text.secondary">
            Dry run passed
            {typeof candidate.confidence === 'number' && ` · ${candidate.confidence.toFixed(2)}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
          {candidate.display_name}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">
          {candidate.name}
        </Typography>
        <Chip size="small" variant="outlined"
              label={`${candidate.kind} · ${candidate.value_type}`} />
        <Chip size="small" variant="outlined" label={perspectiveName} />
      </Box>

      {candidate.english && (
        <Typography variant="body2" sx={{ mt: 1 }}>{candidate.english}</Typography>
      )}

      <Box sx={{ mt: 1.5, p: 1, overflowX: 'auto', ...stromaInset }}>
        <Typography component="pre" variant="caption"
                    sx={{ fontFamily: 'monospace', m: 0, whiteSpace: 'pre-wrap' }}>
          {candidate.formula}
        </Typography>
      </Box>

      {(candidate.notes || []).length > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
            Assumptions I had to make
          </Typography>
          {(candidate.notes || []).map((n, i) => (
            <Typography key={i} variant="caption" component="div">• {n}</Typography>
          ))}
        </Alert>
      )}

      {!ok && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          That definition didn’t run against the warehouse, so it wasn’t saved:
          <Box component="code" sx={{ display: 'block', mt: 0.5, fontSize: 11 }}>
            {validation?.error || 'unknown error'}
          </Box>
        </Alert>
      )}

      {empty && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          The formula ran, but returned no rows — so there are no numbers to
          check it against. Save it if you’re sure of the wording, or edit and
          try again.
        </Alert>
      )}

      {ok && rows.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Sample
            {validation?.dimension ? ` · by ${validation.dimension}` : ''}
            {` · ${validation?.row_count ?? rows.length} rows`}
            {validation?.execution_time_ms != null && ` · ${validation.execution_time_ms}ms`}
          </Typography>
          <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c} sx={{ fontSize: 11, fontWeight: 600 }}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 6).map((r: any, i: number) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c} sx={{ fontSize: 11 }}>
                        {typeof r?.[c] === 'number' ? r[c].toLocaleString() : String(r?.[c] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          disabled={!ok || busy}
          onClick={() => approve(turn.id, question || turn.userText, followUpOf(turn, { sameQuestion: true }))}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {busy ? 'Saving…' : 'Approve — save draft & answer'}
        </Button>
        <Button size="small" disabled={busy} onClick={() => clear(turn.id)}
                sx={{ textTransform: 'none' }}>
          Edit definition
        </Button>
        <Typography variant="caption" color="text.disabled">
          Saves to {perspectiveName} as a draft for data-team review
        </Typography>
      </Box>
    </Paper>
  );
}
